import React from 'react';

// ============================================
// WeatherCard Component
// Displays the weather information in a
// beautiful card-based layout.
// ============================================

function WeatherCard({ data, darkMode }) {
  // Destructure the weather data for easy access
  const {
    city,
    country,
    temperature,
    feelsLike,
    tempMin,
    tempMax,
    condition,
    description,
    icon,
    humidity,
    windSpeed,
    pressure,
    visibility,
  } = data;

  // Get current date and time
  const now = new Date();
  const dateString = now.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const timeString = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  /**
   * Returns a weather emoji based on the condition
   */
  const getWeatherEmoji = () => {
    switch (condition.toLowerCase()) {
      case 'clear':
        return '☀️';
      case 'clouds':
        return '☁️';
      case 'rain':
      case 'drizzle':
        return '🌧️';
      case 'thunderstorm':
        return '⛈️';
      case 'snow':
        return '❄️';
      case 'mist':
      case 'haze':
      case 'fog':
        return '🌫️';
      default:
        return '🌡️';
    }
  };

  return (
    <div className={`weather-card ${darkMode ? 'weather-card-dark' : ''}`}>
      {/* City Name & Country */}
      <div className="weather-header">
        <h2 className="city-name">
          📍 {city}, {country}
        </h2>
        <p className="weather-date">{dateString} • {timeString}</p>
      </div>

      {/* Main Weather Info */}
      <div className="weather-main">
        {/* Weather Icon from OpenWeatherMap */}
        <div className="weather-icon-container">
          <img
            src={`https://openweathermap.org/img/wn/${icon}@4x.png`}
            alt={description}
            className="weather-icon"
          />
        </div>

        {/* Temperature */}
        <div className="temperature-container">
          <span className="temperature">{temperature}°C</span>
          <span className="feels-like">Feels like {feelsLike}°C</span>
          <span className="temp-range">⬆ {tempMax}° ⬇ {tempMin}°</span>
        </div>
      </div>

      {/* Weather Condition */}
      <div className="weather-condition">
        <span className="condition-emoji">{getWeatherEmoji()}</span>
        <span className="condition-text">
          {condition} — {description}
        </span>
      </div>

      {/* Weather Details Grid */}
      <div className="weather-details">
        {/* Humidity */}
        <div className="detail-item">
          <div className="detail-icon-wrapper humidity-icon">
            <svg viewBox="0 0 24 24" className="detail-svg-icon"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" fill="currentColor"/></svg>
          </div>
          <div className="detail-info">
            <span className="detail-value">{humidity}%</span>
            <span className="detail-label">Humidity</span>
          </div>
        </div>

        {/* Wind Speed */}
        <div className="detail-item">
          <div className="detail-icon-wrapper wind-icon">
            <svg viewBox="0 0 24 24" className="detail-svg-icon"><path d="M3 8h10a3 3 0 1 0-3-3M3 12h14a3 3 0 1 1-3 3M3 16h7a3 3 0 1 1-3 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
          <div className="detail-info">
            <span className="detail-value">{windSpeed} m/s</span>
            <span className="detail-label">Wind Speed</span>
          </div>
        </div>

        {/* Pressure */}
        <div className="detail-item">
          <div className="detail-icon-wrapper pressure-icon">
            <svg viewBox="0 0 24 24" className="detail-svg-icon"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2"/><path d="M12 7v5l3 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
          <div className="detail-info">
            <span className="detail-value">{pressure} hPa</span>
            <span className="detail-label">Pressure</span>
          </div>
        </div>

        {/* Visibility */}
        <div className="detail-item">
          <div className="detail-icon-wrapper visibility-icon">
            <svg viewBox="0 0 24 24" className="detail-svg-icon"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" fill="none" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>
          </div>
          <div className="detail-info">
            <span className="detail-value">{visibility ? (visibility / 1000).toFixed(1) : 'N/A'} km</span>
            <span className="detail-label">Visibility</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WeatherCard;
